import { useState, useEffect, useMemo, useContext } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import MDBox from "./components/MDBox";
import "./app.css";
import Sidenav from "./examples/Sidenav";
import theme from "./assets/theme";
import themeDark from "./assets/theme-dark";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import routes from "./routes";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "./context";
import brandWhite from "./assets/images/logo-ct.png";
import brandDark from "./assets/images/logo-ct-dark.png";
import MDSnackbar from "./components/MDSnackbar";
import { LoginFormContext } from "./context/LoginFormContext";
import { AuthRouteJson } from "helpers/constant";
import Loader from "components/MDLoader";
import Booking from "layouts/Page/amenityBooking/booking";
import { logOut } from "Apis/signIn";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { notification, setNotification, isLoading, AppLoader, profileName } =
    useContext(LoginFormContext);

  const LOGOUT_AFTER_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  // const LOGOUT_AFTER_MS = 60 * 1000; // 1 minutes

  function checkAutoLogout() {
    const loginTime = localStorage.getItem("loginTime");
    if (!loginTime) {
      logoutUser();
      return;
    } // not logged in

    const elapsed = Date.now() - parseInt(loginTime, 10);

    if (elapsed >= LOGOUT_AFTER_MS) {
      logoutUser();
    } else {
      // Schedule logout for the remaining time
      const remaining = LOGOUT_AFTER_MS - elapsed;
      setTimeout(logoutUser, remaining);
    }
  }

  async function logoutUser() {
    if (!localStorage.getItem("isLogin")) return;
    AppLoader(true);
    let response = await logOut();
    if (response?.status === 200) {
      localStorage.removeItem("loginTime");
      localStorage.removeItem("authorization");
      localStorage.removeItem("role");
      localStorage.removeItem("modules");
      localStorage.removeItem("isLogin");
      navigate(AuthRouteJson.signin);
      setNotification({
        color: "success",
        icon: "check",
        title: "Success",
        content: response.data.message,
        open: true,
      });
      AppLoader(false);
    } else {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: response.data.message,
        open: true,
      });
      AppLoader(false);
    }
  }

  useEffect(() => {
    checkAutoLogout();
  }, [pathname]);
  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });
  const closeSuccessSB = () => setNotification({ ...notification, open: false });
  const authorized = localStorage.getItem("authorization");
  const location = useLocation();
  const navigate = useNavigate();
  var allow_path = [
    AuthRouteJson.signin,
    AuthRouteJson.forgotpassword,
    AuthRouteJson.otp,
    AuthRouteJson.resetpassword,
  ];
  useEffect(() => {
    if (localStorage.getItem("authorization")) {
      if (allow_path.includes(location.pathname)) {
        return navigate(AuthRouteJson.dashboard);
      }
    } else {
      if (!allow_path.includes(location.pathname)) {
        navigate(AuthRouteJson.signin);
      }
    }
  }, []);

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      {isLoading && <Loader sidenav={miniSidenav && miniSidenav} />}
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          {localStorage.getItem("authorization") && (
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName={profileName}
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}
          {/* {configsButton} */}
        </>
      )}
      <Routes>
        {getRoutes(routes)}
        <Route exact path="amenity/booking/add" element={<Booking />} key="amenity-booking-add" />;
        {authorized && <Route path="*" element={<Navigate to={AuthRouteJson.dashboard} />} />}
      </Routes>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime=""
        open={notification.open}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
    </ThemeProvider>
  );
}
